// src/pages/Desinscription.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { addDesinscription } from "./../../services/desinscriptionServices";
import useScrollToTop from "../../hooks/useScrollToTop";
import {
  FaEnvelope,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

const cx = (...c) => c.filter(Boolean).join(" ");
const normalizeEmail = (v) =>
  String(v || "")
    .trim()
    .toLowerCase();
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// API publique (comme ton composant Products)
const PUBLIC_API = "https://api.monappliclub.com";

function AppMiniList() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchApps = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(
        `${PUBLIC_API}/api/productsapp/allproductsapp`,
      );
      const arr = Array.isArray(data) ? data : [];
      const sorted = [...arr].sort(
        (a, b) => (a?.ordre_app ?? 9999) - (b?.ordre_app ?? 9999),
      );
      setApps(sorted.slice(0, 20)); // sobre + utile
    } catch (e) {
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  return (
    <div className="rounded-3xl border border-black/5 dark:border-white/10 bg-white/80 dark:bg-white/5 backdrop-blur shadow-[0_18px_55px_-40px_rgba(0,0,0,0.55)] overflow-hidden">
      <div className="px-6 py-5 border-b border-black/5 dark:border-white/10">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white">
          Mes applications
        </h3>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Des outils pour clubs sportifs, Comité & Ligues, Omnisports, Mairies, ...
        </p>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="grid grid-cols-1 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5 p-4 animate-pulse"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-black/10 dark:bg-white/10" />
                  <div className="flex-1">
                    <div className="h-3 w-1/2 rounded bg-black/10 dark:bg-white/10" />
                    <div className="mt-2 h-3 w-4/5 rounded bg-black/10 dark:bg-white/10" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : apps.length ? (
          <div className="grid grid-cols-1 gap-3">
            {apps.map((a) => (
              <article
                key={a.id}
                className="rounded-2xl border border-black/5 dark:border-white/10 bg-white/70 dark:bg-white/5 hover:bg-white/90 dark:hover:bg-white/10 transition p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br from-orange-400/25 to-fuchsia-500/20 border border-black/5 dark:border-white/10 grid place-items-center overflow-hidden">
                    {a?.image_url_app ? (
                      <img
                        src={a.image_url_app}
                        alt={a?.nom_app || "Application"}
                        className="h-full w-full object-contain p-1"
                        loading="lazy"
                      />
                    ) : (
                      <div className="text-xs text-slate-500 dark:text-slate-300">
                        —
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                        {a?.nom_app || "Application"}
                      </h4>
                      {a?.lien_url_app ? (
                        <a
                          href={a.lien_url_app}
                          target="_blank"
                          rel="noreferrer"
                          className="shrink-0 text-xs font-semibold rounded-xl px-3 py-1.5
                                     bg-gradient-to-r from-yellow-300 to-orange-500 text-black
                                     hover:brightness-105 transition"
                        >
                          Voir
                        </a>
                      ) : null}
                    </div>

                    {!!a?.description_app && (
                      <p className="mt-1 text-xs text-slate-600 dark:text-slate-300 line-clamp-2">
                        {a.description_app}
                      </p>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-600 dark:text-slate-300">
            La liste des applications n’est pas disponible pour le moment.
            <div className="mt-2">
              <a
                href="https://www.monappliclub.com"
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline"
              >
                Voir mon site
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Desinscription = () => {
  useScrollToTop();

  const [email, setEmail] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState({ type: "idle", text: "" }); // success | info | error | idle

  useEffect(() => {
    // Pré-remplissage si lien du type /desinscription?email=xxx
    const sp = new URLSearchParams(window.location.search);
    const e = normalizeEmail(sp.get("email"));
    if (e) setEmail(e);
  }, []);

  const emailNorm = useMemo(() => normalizeEmail(email), [email]);

  const canSubmit = useMemo(() => {
    return !loading && isValidEmail(emailNorm) && confirmChecked;
  }, [loading, emailNorm, confirmChecked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult({ type: "idle", text: "" });

    if (!isValidEmail(emailNorm)) {
      setResult({
        type: "error",
        text: "Merci de saisir une adresse email valide.",
      });
      return;
    }
    if (!confirmChecked) {
      setResult({
        type: "error",
        text: "Merci de cocher la case de confirmation.",
      });
      return;
    }

    // ✅ sécurité sans ressaisie : un confirm explicite
    const ok = window.confirm(
      `Confirmer la désinscription de :\n\n${emailNorm}\n\nVous ne recevrez plus d’emails.`,
    );
    if (!ok) return;

    try {
      setLoading(true);
      const res = await addDesinscription({ email: emailNorm });

      // Si ton API renvoie status:"already", on gère.
      if (res?.status === "already") {
        setResult({
          type: "info",
          text: "Cet email était déjà désinscrit. Aucun changement.",
        });
      } else {
        setResult({
          type: "success",
          text: "Désinscription confirmée. Vous ne recevrez plus d’emails.",
        });
      }
    } catch (err) {
      setResult({
        type: "error",
        text:
          err?.response?.data?.message ||
          "Erreur lors de la désinscription. Réessayez dans quelques instants.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950 text-slate-900 dark:text-white">
      {/* Bandeau + couleur */}
      <div className="relative h-28 w-full overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900 via-slate-900 to-neutral-700" />
        <div className="absolute -top-24 right-6 h-64 w-64 rounded-full bg-orange-400/25 blur-3xl" />
        <div className="absolute -bottom-20 left-10 h-64 w-64 rounded-full bg-fuchsia-500/20 blur-3xl" />
      </div>

      <div className="mx-auto max-w-6xl px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Card désinscription */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-black/5 dark:border-white/10 bg-white/85 dark:bg-white/5 backdrop-blur shadow-[0_18px_55px_-40px_rgba(0,0,0,0.6)] overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex items-start gap-3">
                  <div className="mt-1 rounded-2xl bg-gradient-to-br from-orange-400/20 to-yellow-400/15 border border-black/5 dark:border-white/10 p-3">
                    <FaEnvelope />
                  </div>
                  <div>
                    <h2 className="text-2xl font-extrabold tracking-tight">
                      Désinscription
                    </h2>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      Saisissez votre email pour ne plus recevoir nos messages.
                    </p>
                  </div>
                </div>

                {/* Résultat */}
                {result.type !== "idle" && (
                  <div
                    className={cx(
                      "mt-5 rounded-2xl border p-4 text-sm",
                      result.type === "success" &&
                        "border-emerald-500/20 bg-emerald-500/10",
                      result.type === "info" &&
                        "border-blue-500/20 bg-blue-500/10",
                      result.type === "error" &&
                        "border-rose-500/20 bg-rose-500/10",
                    )}
                  >
                    <div className="flex items-start gap-3">
                      {result.type === "success" ? (
                        <FaCheckCircle className="mt-0.5 text-emerald-500" />
                      ) : result.type === "error" ? (
                        <FaExclamationTriangle className="mt-0.5 text-rose-500" />
                      ) : (
                        <FaCheckCircle className="mt-0.5 text-blue-400" />
                      )}
                      <div>
                        <div className="font-semibold">
                          {result.type === "success"
                            ? "OK"
                            : result.type === "info"
                              ? "Information"
                              : "Attention"}
                        </div>
                        <div className="mt-1 text-slate-700 dark:text-slate-200">
                          {result.text}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  <label className="block">
                    <span className="text-sm text-slate-700 dark:text-slate-200">
                      Adresse email
                    </span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ex: contact@club.fr"
                      required
                      disabled={loading}
                      className="mt-1 w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white/90 dark:bg-black/20 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-400/50"
                    />
                  </label>

                  <label className="flex items-start gap-3 rounded-2xl border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5 p-4">
                    <input
                      type="checkbox"
                      checked={confirmChecked}
                      onChange={(e) => setConfirmChecked(e.target.checked)}
                      className="mt-1"
                      disabled={loading}
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-200">
                      Je confirme vouloir me désinscrire de la liste de
                      diffusion.
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className={cx(
                      "w-full rounded-2xl px-5 py-3 text-sm font-bold transition",
                      canSubmit
                        ? "bg-gradient-to-r from-yellow-300 to-orange-500 text-black hover:brightness-105"
                        : "bg-black/10 dark:bg-white/10 text-slate-400 cursor-not-allowed",
                    )}
                  >
                    {loading
                      ? "Désinscription..."
                      : "Confirmer la désinscription"}
                  </button>

                  <div className="text-xs text-slate-500 dark:text-slate-400 text-center">
                    Vous pouvez consulter mon site :{" "}
                    <a
                      href="https://www.monappliclub.com"
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold underline"
                    >
                      monappliclub.com
                    </a>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Apps */}
          <div className="lg:col-span-7">
            <AppMiniList />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Desinscription;
